import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

async function postRunCustomModel(id: number): Promise<void> {
  const url = `${POST_URL(id.toString()).trainLanguage}`;
  await Axios.post(url);
}

export function useRunCustomModel() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<BaseError>, number>(
    (id: number) => postRunCustomModel(id),
    {
      onSuccess: (_, id) => {
        // Invalidate specific model details to get updated training status
        queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.detail(id));
        // Invalidate model data
        queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.data(id));
        // Invalidate all list queries to see status changes
        queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.list);
        // Add training-specific invalidation
        queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.training(id));
      },
      onError: (error: AxiosError<BaseError>) => {
        const fallbackMessage = t('an-error-occurred-while-starting-training');
        const data = error?.response?.data;

        let messages: string[] = [];

        if (Array.isArray(data?.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messages = data.detail.map((err: any) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'));
      },
      useErrorBoundary: false
    }
  );
}
